import React, { useRef } from 'react';
import useScrollParallax from '../../../hooks/useScrollParallax';

/**
 * Shared design-system primitives for the Customer Portal.
 * Premium private-banking aesthetic, consistent across all pages.
 *
 * Color tokens:
 *   - ink:       #0A0A0A  (text)
 *   - gold:      #C5A059  (accent)
 *   - gold-bg:   #F4ECD8  (pale gold)
 *   - ivory:     #FAFAF7  (app bg)
 *   - line:      gray-100/200 (borders)
 *
 * Typography:
 *   - headings:  font-heading (Playfair Display)
 *   - body:      sans  (Manrope)
 *   - numerics:  tabular-nums  (lining figures)
 */

// ============================================================
// PageHeader — unified page-top section
// ============================================================
export const PageHeader = ({ title, subtitle, action, children, testid }) => (
  <div className="flex items-end justify-between flex-wrap gap-4 pb-4 sm:pb-6 mb-6 sm:mb-8 border-b border-gray-100" data-testid={testid}>
    <div className="min-w-0">
      <h2 className="font-heading text-2xl sm:text-3xl text-[#0A0A0A] leading-tight">{title}</h2>
      {subtitle && <p className="text-sm text-gray-500 mt-1.5 max-w-2xl">{subtitle}</p>}
      {children}
    </div>
    {action && <div className="flex items-center gap-2 flex-shrink-0">{action}</div>}
  </div>
);

// ============================================================
// DataCard — neutral content card with soft luxury feel
// Optional bgIcon prop renders a large faded SVG as background art.
// ============================================================
export const DataCard = ({ title, action, padding = 'p-5 sm:p-6', className = '', children, testid, bgIcon, bgIconColor = '#C5A059' }) => {
  const bgRef = useRef(null);
  const parallaxOffset = useScrollParallax(bgRef, 0.05);
  return (
    <section
      className={`relative rounded-2xl bg-white ring-1 ring-gray-100/80 ${padding} shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden h-full flex flex-col ${className}`}
      data-testid={testid}
    >
      {/* Subtle radial accent */}
      <div className="absolute inset-0 pointer-events-none opacity-60"
        style={{ background: 'radial-gradient(circle at 100% 0%, rgba(197,160,89,0.05) 0%, transparent 40%)' }} />
      {/* Soft background art (large faded icon) with parallax */}
      {bgIcon && (
        <div
          ref={bgRef}
          className="absolute right-[-30px] bottom-[-20px] pointer-events-none select-none opacity-[0.06] will-change-transform"
          style={{
            color: bgIconColor,
            width: '220px',
            height: '220px',
            transform: `translateY(${parallaxOffset}px)`,
          }}
          aria-hidden="true"
        >
          {bgIcon}
        </div>
      )}
      <div className="relative flex-1 flex flex-col">
        {(title || action) && (
          <header className="flex items-center justify-between gap-3 mb-4">
            {title && <h3 className="font-heading text-base sm:text-lg text-[#0A0A0A] tracking-tight">{title}</h3>}
            {action && <div className="text-sm">{action}</div>}
          </header>
        )}
        <div className="flex-1 flex flex-col">{children}</div>
      </div>
    </section>
  );
};

// ============================================================
// StatCard — KPI tile with GUARANTEED equal heights & baselines
// Strict 3-row grid: header[40px] · value[grow] · sub[18px]
// Soft white background with faded BgArt icon, accent color only on value & icon.
// ============================================================
export const StatCard = ({
  label,
  value,
  sub,
  icon,
  variant = 'default',
  testid,
  trend,
  bgIcon,
}) => {
  const variants = {
    default: { iconBg: 'bg-[#F4ECD8]/80', iconColor: '#C5A059', valueColor: 'text-[#0A0A0A]', glow: 'hover:shadow-[0_24px_60px_-20px_rgba(197,160,89,0.30)]', bgIconColor: '#C5A059' },
    gold:    { iconBg: 'bg-[#F4ECD8]',     iconColor: '#C5A059', valueColor: 'text-[#C5A059]', glow: 'hover:shadow-[0_24px_60px_-20px_rgba(197,160,89,0.40)]', bgIconColor: '#C5A059' },
    success: { iconBg: 'bg-emerald-50',    iconColor: '#059669', valueColor: 'text-emerald-700', glow: 'hover:shadow-[0_24px_60px_-20px_rgba(16,185,129,0.32)]', bgIconColor: '#15803d' },
    sky:     { iconBg: 'bg-sky-50',        iconColor: '#0369A1', valueColor: 'text-sky-700', glow: 'hover:shadow-[0_24px_60px_-20px_rgba(2,132,199,0.30)]', bgIconColor: '#0369A1' },
    danger:  { iconBg: 'bg-red-50',        iconColor: '#B91C1C', valueColor: 'text-red-700', glow: 'hover:shadow-[0_24px_60px_-20px_rgba(220,38,38,0.26)]', bgIconColor: '#B91C1C' },
    neutral: { iconBg: 'bg-gray-100',      iconColor: '#525252', valueColor: 'text-gray-700', glow: 'hover:shadow-[0_24px_60px_-20px_rgba(0,0,0,0.14)]', bgIconColor: '#525252' },
  };
  const v = variants[variant] || variants.default;
  const bgRef = useRef(null);
  const parallaxOffset = useScrollParallax(bgRef, 0.06);

  return (
    <div
      className={`group relative rounded-2xl bg-white ring-1 ring-gray-100/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all duration-500 ${v.glow} hover:-translate-y-1 overflow-hidden`}
      data-testid={testid}
    >
      {/* Faded background art (large SVG icon) — with parallax */}
      {bgIcon && (
        <div
          ref={bgRef}
          className="absolute right-[-25px] bottom-[-25px] pointer-events-none select-none opacity-[0.07] transition-opacity duration-500 group-hover:opacity-[0.14] will-change-transform"
          style={{
            color: v.bgIconColor,
            width: '160px',
            height: '160px',
            transform: `translateY(${parallaxOffset}px)`,
          }}
          aria-hidden="true"
        >
          {bgIcon}
        </div>
      )}
      {/* Subtle hover bloom */}
      <div
        className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-2xl pointer-events-none"
        style={{ background: `radial-gradient(circle, ${v.bgIconColor}33, transparent 70%)` }}
      />

      <div
        className="relative grid p-4 sm:p-5 h-full"
        style={{ gridTemplateRows: '40px 1fr 18px', rowGap: '14px' }}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          {icon && (
            <div className={`w-10 h-10 rounded-xl ${v.iconBg} flex items-center justify-center flex-shrink-0`}>
              <span style={{ color: v.iconColor }}>{icon}</span>
            </div>
          )}
          <p className="text-[10px] uppercase tracking-[0.1em] text-gray-500 font-semibold leading-tight truncate flex-1">
            {label}
          </p>
        </div>

        <div className="flex items-end">
          <p className={`font-heading text-2xl sm:text-[26px] lg:text-[28px] ${v.valueColor} leading-none tabular-nums whitespace-nowrap`}>
            {value}
          </p>
          {trend && (
            <span className={`ml-2 mb-1 text-[10px] tabular-nums font-semibold px-1.5 py-0.5 rounded ${trend.positive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {trend.value}
            </span>
          )}
        </div>

        <p className="text-[11px] text-gray-500 leading-tight truncate font-medium">{sub || ''}</p>
      </div>
    </div>
  );
};

// ============================================================
// StatusChip — unified status pill
// ============================================================
const STATUS_STYLES = {
  active:    'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100',
  due:       'bg-[#FAF1D9] text-[#9C7F3F] ring-1 ring-[#E8D9A8]',
  pending:   'bg-amber-50 text-amber-700 ring-1 ring-amber-100',
  review:    'bg-sky-50 text-sky-700 ring-1 ring-sky-100',
  rejected:  'bg-red-50 text-red-700 ring-1 ring-red-100',
  revoked:   'bg-gray-100 text-gray-600 ring-1 ring-gray-200',
  confirmed: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100',
  neutral:   'bg-gray-50 text-gray-600 ring-1 ring-gray-200',
};

export const StatusChip = ({ tone = 'neutral', children, testid, className = '' }) => (
  <span
    className={`inline-flex items-center gap-1 text-[10px] sm:text-[11px] px-2.5 py-1 rounded-full font-medium ${STATUS_STYLES[tone] || STATUS_STYLES.neutral} ${className}`}
    data-testid={testid}
  >
    {children}
  </span>
);

// ============================================================
// ActionButton — primary / secondary / ghost / danger
// ============================================================
export const ActionButton = ({
  variant = 'secondary',
  size = 'sm',
  icon,
  children,
  className = '',
  ...rest
}) => {
  const sizes = {
    xs: 'text-[11px] px-2.5 py-1.5',
    sm: 'text-xs px-3 py-2',
    md: 'text-sm px-4 py-2.5',
  };
  const variants = {
    primary:   'bg-[#0A0A0A] text-white hover:bg-[#1F1F1F] border border-transparent',
    gold:      'bg-[#C5A059] text-white hover:bg-[#B08E48] border border-transparent',
    secondary: 'bg-white text-gray-800 border border-gray-200 hover:bg-gray-50 hover:border-gray-300',
    ghost:     'bg-transparent text-gray-700 border border-transparent hover:bg-gray-100',
    danger:    'bg-white text-red-600 border border-red-200 hover:bg-red-50',
  };
  return (
    <button
      className={`vp-tactile vp-focus inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${sizes[size]} ${variants[variant]} ${className}`}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
};

// ============================================================
// EmptyState — premium "no items" placeholder
// ============================================================
export const EmptyState = ({ icon, title, description, action, testid }) => (
  <div className="rounded-2xl bg-white border border-gray-100 text-center py-12 sm:py-16 px-6" data-testid={testid}>
    {icon && (
      <div className="w-16 h-16 mx-auto mb-5 rounded-full flex items-center justify-center bg-[#F4ECD8]">
        <span className="text-[#C5A059]">{icon}</span>
      </div>
    )}
    <h3 className="font-heading text-lg sm:text-xl text-[#0A0A0A] mb-2">{title}</h3>
    {description && <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">{description}</p>}
    {action}
  </div>
);

// ============================================================
// FilterTabs — pill segmented control
// ============================================================
export const FilterTabs = ({ tabs, active, onChange, testid }) => (
  <div className="flex items-center gap-1 overflow-x-auto pb-1 -mx-1 px-1" data-testid={testid}>
    {tabs.map((t) => (
      <button
        key={t.id}
        onClick={() => onChange(t.id)}
        className={`whitespace-nowrap inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium px-3 sm:px-4 py-2 rounded-full transition-colors
          ${active === t.id
            ? 'bg-[#0A0A0A] text-white'
            : 'text-gray-600 hover:text-[#0A0A0A] hover:bg-gray-100'}`}
        data-testid={`filter-tab-${t.id}`}
      >
        {t.label}
        {t.count !== undefined && (
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full tabular-nums ${active === t.id ? 'bg-white/15 text-white' : 'bg-gray-200 text-gray-700'}`}>
            {t.count}
          </span>
        )}
      </button>
    ))}
  </div>
);

// ============================================================
// SearchInput — minimal premium search
// ============================================================
export const SearchInput = ({ value, onChange, placeholder = 'Suchen…', testid, className = '' }) => (
  <div className={`relative ${className}`}>
    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
    </svg>
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/15 transition"
    />
  </div>
);

// ============================================================
// InfoBanner — bank-grade notice / security message
// ============================================================
export const InfoBanner = ({ icon, title, description, testid, tone = 'gold' }) => {
  const tones = {
    gold: {
      bg: 'bg-gradient-to-br from-[#FDF8EC] via-white to-[#F4ECD8]/60',
      ring: 'ring-[#E8D9A8]',
      iconBg: 'bg-[#0A0A0A]',
      iconColor: '#C5A059',
      pattern: 'radial-gradient(circle at 100% 0%, rgba(197,160,89,0.18) 0%, transparent 45%), radial-gradient(circle at 0% 100%, rgba(197,160,89,0.10) 0%, transparent 35%)',
    },
    info: {
      bg: 'bg-gradient-to-br from-sky-50/80 via-white to-sky-50/40',
      ring: 'ring-sky-100',
      iconBg: 'bg-sky-100',
      iconColor: '#0369A1',
      pattern: 'radial-gradient(circle at 100% 0%, rgba(2,132,199,0.12) 0%, transparent 45%)',
    },
    warn: {
      bg: 'bg-gradient-to-br from-amber-50 via-white to-amber-50/50',
      ring: 'ring-amber-200',
      iconBg: 'bg-amber-100',
      iconColor: '#9A6B00',
      pattern: 'radial-gradient(circle at 100% 0%, rgba(217,119,6,0.12) 0%, transparent 45%)',
    },
  };
  const t = tones[tone] || tones.gold;
  return (
    <div className={`relative rounded-2xl ${t.bg} ring-1 ${t.ring} p-5 sm:p-6 flex items-start gap-4 overflow-hidden`} data-testid={testid}>
      <div className="absolute inset-0 pointer-events-none opacity-90" style={{ background: t.pattern }} />
      {icon && (
        <div className={`relative w-12 h-12 rounded-2xl ${t.iconBg} flex items-center justify-center flex-shrink-0 shadow-[inset_0_1px_0_rgba(255,255,255,0.1),0_4px_12px_rgba(0,0,0,0.08)]`}>
          <span style={{ color: t.iconColor }}>{icon}</span>
        </div>
      )}
      <div className="relative min-w-0">
        <h4 className="font-heading text-base sm:text-lg text-[#0A0A0A] mb-1.5 tracking-tight">{title}</h4>
        <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
      </div>
    </div>
  );
};

// ============================================================
// Icons — shared SVG icons
// ============================================================
export const Icons = {
  shield: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3l8 4v6c0 4.5-3.5 8.4-8 9-4.5-.6-8-4.5-8-9V7l8-4z"/><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4"/></svg>,
  plus:   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14"/></svg>,
  check:  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>,
  clock:  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>,
  x:      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>,
  arrow:  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/></svg>,
  doc:    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m-6-8h6M5 21h14a2 2 0 002-2V7l-5-5H7a2 2 0 00-2 2v15z"/></svg>,
  refresh:<svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582M4.418 15A8.001 8.001 0 0019.418 15M20 20v-5h-.582M19.582 9A8.001 8.001 0 004.582 9"/></svg>,
  trash:  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3"/></svg>,
  eye:    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.5 12C4 7.5 7.5 5 12 5s8 2.5 9.5 7c-1.5 4.5-5 7-9.5 7s-8-2.5-9.5-7z"/></svg>,
};

// ============================================================
// Decorative background icons (large, faded — used as card art)
// Each unique & thematically tied to its context.
// ============================================================
export const BgArt = {
  // Coins — for accrued / interest
  coins: <svg viewBox="0 0 100 100" fill="currentColor" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMaxYMax meet">
    <ellipse cx="35" cy="40" rx="22" ry="8" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M13 40v18c0 4.4 9.85 8 22 8s22-3.6 22-8V40" stroke="currentColor" strokeWidth="2" fill="none"/>
    <ellipse cx="65" cy="65" rx="22" ry="8" stroke="currentColor" strokeWidth="2" fill="none"/>
    <path d="M43 65v18c0 4.4 9.85 8 22 8s22-3.6 22-8V65" stroke="currentColor" strokeWidth="2" fill="none"/>
  </svg>,
  // Line chart — for yield / performance
  chart: <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 80 L30 60 L50 70 L70 35 L90 20" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M70 35 L90 35 L90 20" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="10" y1="90" x2="90" y2="90" strokeLinecap="round"/>
  </svg>,
  // Vault / safe — for total deposit
  vault: <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
    <rect x="15" y="20" width="70" height="60" rx="6"/>
    <circle cx="50" cy="50" r="14"/>
    <circle cx="50" cy="50" r="3" fill="currentColor"/>
    <line x1="50" y1="36" x2="50" y2="30"/>
    <line x1="50" y1="70" x2="50" y2="64"/>
    <line x1="36" y1="50" x2="30" y2="50"/>
    <line x1="70" y1="50" x2="64" y2="50"/>
  </svg>,
  // Document — for paperwork / docs
  doc: <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
    <path d="M25 12 L65 12 L80 28 L80 88 L25 88 Z" strokeLinejoin="round"/>
    <path d="M65 12 L65 28 L80 28"/>
    <line x1="35" y1="42" x2="70" y2="42"/>
    <line x1="35" y1="55" x2="70" y2="55"/>
    <line x1="35" y1="68" x2="60" y2="68"/>
  </svg>,
  // Shield with check — for active authorizations / security
  shield: <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
    <path d="M50 10 L82 22 L82 50 C82 70 70 84 50 90 C30 84 18 70 18 50 L18 22 Z" strokeLinejoin="round"/>
    <path d="M35 50 L46 60 L66 40" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>,
  // Calendar — for due dates / schedule
  calendar: <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
    <rect x="15" y="22" width="70" height="60" rx="6"/>
    <line x1="15" y1="38" x2="85" y2="38"/>
    <line x1="32" y1="22" x2="32" y2="14" strokeLinecap="round"/>
    <line x1="68" y1="22" x2="68" y2="14" strokeLinecap="round"/>
    <circle cx="32" cy="55" r="2" fill="currentColor"/>
    <circle cx="50" cy="55" r="2" fill="currentColor"/>
    <circle cx="68" cy="55" r="2" fill="currentColor"/>
    <circle cx="32" cy="70" r="2" fill="currentColor"/>
  </svg>,
  // Bank building — for confirmed / outgoing transfers
  bank: <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 35 L50 12 L90 35" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="10" y1="42" x2="90" y2="42" strokeLinecap="round"/>
    <line x1="20" y1="42" x2="20" y2="78"/>
    <line x1="40" y1="42" x2="40" y2="78"/>
    <line x1="60" y1="42" x2="60" y2="78"/>
    <line x1="80" y1="42" x2="80" y2="78"/>
    <line x1="8" y1="82" x2="92" y2="82" strokeLinecap="round"/>
    <line x1="8" y1="90" x2="92" y2="90" strokeLinecap="round"/>
  </svg>,
  // Hourglass — for pending / waiting state
  hourglass: <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
    <line x1="22" y1="14" x2="78" y2="14" strokeLinecap="round"/>
    <line x1="22" y1="86" x2="78" y2="86" strokeLinecap="round"/>
    <path d="M28 14 L28 30 L50 50 L72 30 L72 14" strokeLinejoin="round"/>
    <path d="M28 86 L28 70 L50 50 L72 70 L72 86" strokeLinejoin="round"/>
    <path d="M40 25 L60 25 L50 40 Z" fill="currentColor" stroke="none" opacity="0.6"/>
  </svg>,
  // Stamp / seal — for rejected (gestempelt abgelehnt)
  stamp: <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
    <path d="M30 20 L70 20 L70 35 L80 50 L80 65 L20 65 L20 50 L30 35 Z" strokeLinejoin="round"/>
    <line x1="12" y1="72" x2="88" y2="72" strokeLinecap="round" strokeWidth="3"/>
    <line x1="20" y1="80" x2="80" y2="80" strokeLinecap="round"/>
    <line x1="40" y1="32" x2="60" y2="32"/>
    <line x1="38" y1="42" x2="62" y2="42"/>
    <line x1="42" y1="52" x2="58" y2="52"/>
  </svg>,
  // Ban / canceled — for revoked
  ban: <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="38"/>
    <line x1="24" y1="24" x2="76" y2="76" strokeLinecap="round"/>
  </svg>,
  // Compass / grid — for quick access
  compass: <svg viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="2" xmlns="http://www.w3.org/2000/svg">
    <circle cx="50" cy="50" r="38"/>
    <path d="M50 22 L58 50 L50 78 L42 50 Z" strokeLinejoin="round" fill="currentColor" fillOpacity="0.15"/>
    <circle cx="50" cy="50" r="3" fill="currentColor"/>
    <line x1="50" y1="14" x2="50" y2="20" strokeLinecap="round"/>
    <line x1="50" y1="80" x2="50" y2="86" strokeLinecap="round"/>
    <line x1="14" y1="50" x2="20" y2="50" strokeLinecap="round"/>
    <line x1="80" y1="50" x2="86" y2="50" strokeLinecap="round"/>
  </svg>,
};
export const Avatar = ({ name = '', size = 44, className = '' }) => {
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map(p => p[0]?.toUpperCase()).join('') || '?';
  return (
    <div
      className={`flex items-center justify-center rounded-full bg-[#0A0A0A] text-white font-medium flex-shrink-0 ${className}`}
      style={{ width: size, height: size, fontSize: size * 0.36 }}
      aria-hidden="true"
    >
      {initials}
    </div>
  );
};
